import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getDistributionPreview, executeAnnualDistribution } from '../../services/rewardsService';
import toast from 'react-hot-toast';

// Available financial years to choose from (current + previous 3 + next 1)
function buildFYOptions() {
  const now = new Date();
  const month = now.getUTCMonth();
  const year = now.getUTCFullYear();
  const currentStart = month >= 6 ? year : year - 1;
  const options = [];
  for (let offset = 1; offset >= -3; offset--) {
    const start = currentStart + offset;
    options.push(`${start}-${start + 1}`);
  }
  return options;
}

function currentFY() {
  const now = new Date();
  const month = now.getUTCMonth();
  const year = now.getUTCFullYear();
  const start = month >= 6 ? year : year - 1;
  return `${start}-${start + 1}`;
}

export default function AnnualDistributionPanel() {
  const { userProfile } = useAuth();
  const isSuperAdmin = userProfile?.role === 'superAdmin';

  const [financialYear, setFinancialYear] = useState(currentFY());
  const [netProfitInput, setNetProfitInput] = useState('');
  const [sharePercentInput, setSharePercentInput] = useState('75');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [typedConfirm, setTypedConfirm] = useState('');

  const fyOptions = useMemo(() => buildFYOptions(), []);

  const netProfitAUD = parseFloat(netProfitInput) || 0;
  const sharePercent = parseFloat(sharePercentInput) || 0;

  // Auto-load preview whenever inputs change (debounced)
  useEffect(() => {
    if (!financialYear || netProfitAUD <= 0 || sharePercent <= 0) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    const t = setTimeout(async () => {
      try {
        const data = await getDistributionPreview({ financialYear, netProfitAUD, sharePercent });
        if (!cancelled) setPreview(data);
      } catch (e) {
        if (!cancelled) toast.error(e.message || 'Preview failed');
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [financialYear, netProfitAUD, sharePercent]);

  if (!isSuperAdmin) {
    return (
      <div className="p-10 text-center">
        <div className="text-5xl mb-4">🔒</div>
        <h2 className="font-serif">Super Admin Only</h2>
        <p className="text-body">Annual distribution requires super-admin access.</p>
      </div>
    );
  }

  const handleExecute = async () => {
    if (typedConfirm !== 'DISTRIBUTE') {
      toast.error('Type DISTRIBUTE to confirm');
      return;
    }
    setExecuting(true);
    try {
      const result = await executeAnnualDistribution({
        financialYear,
        netProfitAUD,
        sharePercent,
        typedConfirmation: 'DISTRIBUTE',
      });
      toast.success(`Distributed $${result.totalDistributedAUD.toFixed(2)} to ${result.eligibleDriverCount} drivers`);
      setShowConfirm(false);
      setTypedConfirm('');
      // Reload preview to show executed state
      const data = await getDistributionPreview({ financialYear, netProfitAUD, sharePercent });
      setPreview(data);
    } catch (e) {
      toast.error(e.message || 'Distribution failed');
    }
    setExecuting(false);
  };

  return (
    <div>
      <h1 className="font-serif mt-0 mb-2">Annual Distribution</h1>
      <p className="text-body text-[0.9rem] mb-6">
        Distribute the configured profit share across drivers based on points earned in the financial year.
        Each FY can be executed only once.
      </p>

      {/* Inputs */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-4">
        <h3 className="mt-0 mb-4">Inputs</h3>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-[0.85rem] font-semibold text-body mb-1">Financial Year</label>
            <select value={financialYear} onChange={(e) => setFinancialYear(e.target.value)}
              className="w-full px-3 py-2.5 rounded-[10px] border-[1.5px] border-slate-200 text-[0.95rem] bg-white">
              {fyOptions.map((fy) => <option key={fy} value={fy}>{fy}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[0.85rem] font-semibold text-body mb-1">Net Profit ($AUD)</label>
            <input type="number" min="0" step="0.01" value={netProfitInput} onChange={(e) => setNetProfitInput(e.target.value)}
              placeholder="e.g. 1000000"
              className="w-full px-3 py-2.5 rounded-[10px] border-[1.5px] border-slate-200 text-[0.95rem]" />
            <div className="text-[0.72rem] text-muted mt-1">After all expenses and taxes.</div>
          </div>
          <div>
            <label className="block text-[0.85rem] font-semibold text-body mb-1">Share (%)</label>
            <input type="number" min="0" max="100" step="0.01" value={sharePercentInput} onChange={(e) => setSharePercentInput(e.target.value)}
              className="w-full px-3 py-2.5 rounded-[10px] border-[1.5px] border-slate-200 text-[0.95rem]" />
          </div>
        </div>
      </div>

      {/* Preview summary */}
      {previewLoading && <div className="p-6 text-center text-muted">Calculating preview...</div>}

      {preview && !previewLoading && (
        <>
          <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            <SummaryCard label="Pool" value={`$${preview.poolAUD.toFixed(2)}`} colour="#2dd4a8" />
            <SummaryCard label="Total Points" value={preview.totalPointsAllDrivers.toLocaleString()} />
            <SummaryCard label="Eligible Drivers" value={preview.eligibleDriverCount} />
            <SummaryCard label="Total Allocated" value={`$${preview.totalAllocated.toFixed(2)}`} />
            <SummaryCard label="Remainder" value={`$${preview.remainderAUD.toFixed(2)}`} sub="unpaid (cents rounding)" />
          </div>

          {preview.alreadyExecuted && (
            <div className="p-4 rounded-xl mb-4" style={{ background: '#fef3cd', border: '1px solid #fbbf24', color: '#92400e' }}>
              <strong>Already executed.</strong> Distribution for FY {preview.financialYear} has been completed. To run again you would need to delete the
              <code className="mx-1">profitDistributions/{preview.financialYear}</code> doc — but this is dangerous as wallet credits cannot be reversed.
            </div>
          )}

          {/* Driver table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-auto mb-4">
            <table className="w-full border-collapse text-[0.85rem]">
              <thead>
                <tr className="bg-slate-50">
                  {['Driver', 'Points', 'Trip pts', 'Referral pts', 'Penalties', 'Share %', 'Amount'].map((h) => (
                    <th key={h} className="px-3.5 py-2.5 text-left font-semibold text-body border-b border-slate-200">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.drivers.map((d) => (
                  <tr key={d.driverId} className="border-b border-slate-100" style={{ opacity: d.amountAUD === 0 ? 0.5 : 1 }}>
                    <td className="px-3.5 py-2.5">
                      <div className="font-semibold">{d.firstName || '—'} {d.lastName?.[0] || ''}.</div>
                      <div className="text-[0.75rem] text-muted">{d.referenceNumber || '—'}</div>
                    </td>
                    <td className="px-3.5 py-2.5 font-semibold">
                      {d.clampedPoints}
                      {d.points < 0 && <span className="text-[0.7rem] text-red-600 ml-1">({d.points})</span>}
                    </td>
                    <td className="px-3.5 py-2.5">{d.tripPoints}</td>
                    <td className="px-3.5 py-2.5">{d.referralPoints}</td>
                    <td className="px-3.5 py-2.5" style={{ color: d.cancellationPenalties > 0 ? '#ff6b6b' : '#94a3b8' }}>
                      {d.cancellationPenalties > 0 ? `-${d.cancellationPenalties}` : '—'}
                    </td>
                    <td className="px-3.5 py-2.5">{(d.sharePct * 100).toFixed(2)}%</td>
                    <td className="px-3.5 py-2.5 font-bold" style={{ color: d.amountAUD > 0 ? '#2dd4a8' : '#94a3b8' }}>
                      ${d.amountAUD.toFixed(2)}
                    </td>
                  </tr>
                ))}
                {preview.drivers.length === 0 && (
                  <tr><td colSpan={7} className="p-8 text-center text-muted">No drivers with points in this FY</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Execute button */}
          {!preview.alreadyExecuted && preview.eligibleDriverCount > 0 && (
            <button onClick={() => setShowConfirm(true)}
              className="px-6 py-3 rounded-[10px] border-0 bg-red-600 text-white cursor-pointer font-bold text-[0.95rem]">
              Execute Distribution
            </button>
          )}
        </>
      )}

      {!preview && !previewLoading && netProfitAUD === 0 && (
        <div className="p-8 text-center text-muted bg-slate-50 rounded-xl">
          Enter a net profit amount above to see the distribution preview.
        </div>
      )}

      {/* Confirmation modal */}
      {showConfirm && preview && (
        <div className="fixed inset-0 flex items-center justify-center z-[1000] p-5" style={{ background: 'rgba(10,22,40,0.6)' }} onClick={() => { if (!executing) setShowConfirm(false); }}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-7 max-w-[500px] w-full">
            <h3 className="font-serif mt-0 mb-2 text-red-600">Execute Distribution?</h3>
            <p className="text-body text-[0.9rem] mb-4 leading-relaxed">
              You are about to distribute <strong>${preview.totalAllocated.toFixed(2)}</strong> across <strong>{preview.eligibleDriverCount}</strong> drivers
              for FY <strong>{preview.financialYear}</strong>. Wallet credits will be applied immediately. <strong className="text-red-600">This cannot be undone.</strong>
            </p>
            <p className="text-[0.85rem] text-body mb-2">Type <code className="bg-slate-50 px-1.5 py-0.5 rounded font-bold">DISTRIBUTE</code> to confirm:</p>
            <input type="text" value={typedConfirm} onChange={(e) => setTypedConfirm(e.target.value)} autoFocus
              placeholder="DISTRIBUTE"
              className="w-full px-3 py-2.5 rounded-[10px] border-[1.5px] border-slate-200 text-[0.95rem] font-mono mb-4" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setShowConfirm(false); setTypedConfirm(''); }} disabled={executing}
                className="px-[18px] py-2.5 rounded-[10px] border-[1.5px] border-slate-200 bg-white cursor-pointer font-semibold">Cancel</button>
              <button onClick={handleExecute} disabled={executing || typedConfirm !== 'DISTRIBUTE'}
                className="px-[18px] py-2.5 rounded-[10px] border-0 text-white font-bold"
                style={{
                  background: typedConfirm === 'DISTRIBUTE' ? '#dc2626' : '#e2e8f0',
                  cursor: typedConfirm === 'DISTRIBUTE' ? 'pointer' : 'not-allowed',
                }}>
                {executing ? 'Distributing...' : 'Execute Now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, sub, colour }) {
  return (
    <div className="p-4 rounded-xl bg-white border border-slate-200">
      <div className="text-[0.78rem] text-body mb-1">{label}</div>
      <div className="text-[1.4rem] font-extrabold" style={{ color: colour || '#0a1628' }}>{value}</div>
      {sub && <div className="text-[0.72rem] text-muted mt-1">{sub}</div>}
    </div>
  );
}